import React from 'react';
import { useNavigate } from 'react-router-dom';

const CommunityFoundationReport = () => {
  const navigate = useNavigate();

  const reportSections = [
    {
      title: "Finished Building + Tower",
      image: "https://customer-assets.emergentagent.com/job_faith-waves-3/artifacts/g6vew032_Finshed-Building%2BTower.jpg",
      link: "https://www.dropbox.com/scl/fo/9yswxd5y1oge6c9p7yixo/AGvN5lT-9bhnySD2_xcqQug?rlkey=5qawmm87ax5wkqvq4fbcwraon&st=t5uzu096&dl=0",
      description: "Photos and documentation of the completed radio station building and transmission tower on Betche Hill.",
      icon: "🏗️"
    },
    {
      title: "Kioo Video",
      image: "https://customer-assets.emergentagent.com/job_faith-waves-3/artifacts/uv7t4637_Kioo-video.png",
      link: "https://www.dropbox.com/scl/fo/g947noafol5nvbfzmka8a/AMfqmquHllAYiMMKDQ6VURE?rlkey=500dgfsh5n8q72bw6kwuqemrl&st=34mtkvm5&dl=0",
      description: "Video documentation of the Kioo Radio launch, community impact, and station operations.",
      icon: "🎥"
    },
    {
      title: "Kioo Radio Grant Report",
      image: "https://customer-assets.emergentagent.com/job_faith-waves-3/artifacts/3ic6f39i_DRAFT%20KIOO%20Report-text.png",
      link: "https://www.dropbox.com/scl/fo/ka9zycbyzpjw6hbm7rkh6/ADQr3wX3UxhVMzsxzIq9e4E?rlkey=rkm8p1mad83pcg2jszl0tcbb3&st=4oa9o5qw&dl=0",
      description: "Detailed narrative report documenting the launch activities, outcomes, and stewardship summary for Community Foundation of the North State.",
      icon: "📄"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Header Section */}
      <section className="bg-gradient-to-r from-kioo-primary to-kioo-secondary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/reports')}
            className="mb-6 flex items-center text-white hover:text-green-100 transition-colors group"
          >
            <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </button>

          <div className="text-center">
            <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
              <span className="text-sm font-semibold">Community Foundation of the North State</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              📊 Grant Report Resources
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Comprehensive documentation of the support provided for the launch of Kioo Radio 98.1FM, 
              including activities, outcomes, and stewardship summary.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction Card */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 mb-12 border-2 border-blue-100">
            <div className="flex items-start space-x-4">
              <div className="text-4xl">ℹ️</div>
              <div>
                <h2 className="text-2xl font-bold text-kioo-dark mb-3">About This Report</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This report documents the successful launch and establishment of Kioo Radio 98.1FM, 
                  made possible through the generous support of the Community Foundation of the North State.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Click on any section below to access photos, videos, and detailed documentation via Dropbox.
                </p>
              </div>
            </div>
          </div>

          {/* Report Sections Grid */}
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {reportSections.map((section, index) => (
              <a
                key={index}
                href={section.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-kioo-primary cursor-pointer"
              >
                {/* Image Container */}
                <div className="relative h-64 overflow-hidden bg-gray-100">
                  <img 
                    src={section.image}
                    alt={section.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    style={index === 2 ? { objectPosition: 'center top' } : {}}
                  />
                  
                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center">
                      <div className="text-5xl mb-3">📂</div>
                      <p className="text-lg font-bold">Click to Open Folder</p>
                    </div>
                  </div>

                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4 bg-white rounded-full p-3 shadow-lg">
                    <span className="text-2xl">{section.icon}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                    {section.description}
                  </p>

                  {/* View Button */}
                  <div className="flex items-center text-kioo-primary font-semibold group-hover:translate-x-2 transition-transform">
                    <span>Open in Dropbox</span>
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>

                {/* Footer Badge */}
                <div className="bg-green-50 border-t border-green-100 px-6 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-green-700 font-medium">✅ Available Now</span>
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Additional Information */}
          <div className="mt-12 bg-gray-50 rounded-xl p-8 border border-gray-200">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-kioo-dark mb-4">📧 Need More Information?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed max-w-2xl mx-auto">
                For additional documentation, detailed reports, or specific questions about this grant project, 
                please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="mailto:admin@kiooradio.org"
                  className="inline-flex items-center bg-kioo-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-kioo-secondary transition-colors shadow-md"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Us
                </a>
                <a 
                  href="tel:+231887981981"
                  className="inline-flex items-center bg-white text-kioo-primary border-2 border-kioo-primary px-6 py-3 rounded-lg font-semibold hover:bg-kioo-primary hover:text-white transition-all"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Call Us
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Partnership Acknowledgment */}
      <section className="py-12 bg-kioo-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-4xl mb-4">🤝</div>
          <h2 className="text-3xl font-bold mb-4">Partnership Acknowledgment</h2>
          <p className="text-xl text-green-100 leading-relaxed">
            We are deeply grateful to the Community Foundation of the North State for their vision and 
            partnership in making Kioo Radio 98.1FM a reality. Together, we are bringing the gift of 
            good news to the Makona River Region.
          </p>
        </div>
      </section>

    </div>
  );
};

export default CommunityFoundationReport;
